/**
 * <anzu-hold> — the sticky offset for a held A+ panel.
 *
 * The showcase panel is a short stack: a title, a video, two lines and a control. On a desktop
 * that stack is most of a window and the stylesheet can give it the screen outright. On a phone
 * it is about two thirds of one, and giving it the screen there costs something the desktop
 * never pays.
 *
 * A box that is a screen tall with its content centred inside it is two things at once: it is
 * the right picture while it is HELD, and it is a screen-tall run of empty background while it
 * is arriving. Entering the section, the top half of that emptiness scrolls past before the
 * title appears — measured at 143px on a 375 phone, against 64px of section padding — and what
 * the reader sees is not a section starting, it is a gap between two sections.
 *
 * So the panel is only as tall as itself and the centring moves off the box and onto the sticky
 * offset. In the flow the title now arrives one padding after the seam; held, the offset puts
 * the stack in the middle of the band the reader can actually see. The pinned picture is
 * unchanged — it is the same stack in the same place — and the lead-in is gone.
 *
 * That offset is a number CSS cannot write: it needs the stack's own height. Same trade the
 * connectivity rail makes, for the same reason and with the same arithmetic — see
 * assets/anzu-aplus-rail.js.
 */
(function () {
  if (typeof customElements === 'undefined' || customElements.get('anzu-hold')) return;

  var NARROW = '(max-width: 749px)';

  class AnzuHold extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;
      this.booted = true;

      this.section = this.closest('.aplus-show');
      this.narrow = window.matchMedia ? window.matchMedia(NARROW) : null;
      this.queue = this.queue.bind(this);
      this.raf = 0;
      this.at = null;

      window.addEventListener('resize', this.queue, { passive: true });
      window.addEventListener('orientationchange', this.queue, { passive: true });
      if (this.narrow && this.narrow.addEventListener) this.narrow.addEventListener('change', this.queue);

      /* The stack's height is not settled when this runs: the webfont has not landed, the
         statement has not taken its final number of lines, and the panel's ratio only becomes a
         height once the column has a width. Watching the element itself catches all three
         without needing to know which one moved. Writing `top` cannot change that height, so
         there is no loop to guard against. */
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(this.queue);
        this.ro.observe(this);
      }

      this.measure();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.queue);
      window.removeEventListener('orientationchange', this.queue);
      if (this.narrow && this.narrow.removeEventListener) this.narrow.removeEventListener('change', this.queue);
      if (this.ro) this.ro.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.booted = false;
    }

    queue() {
      if (this.raf) return;
      var self = this;
      this.raf = requestAnimationFrame(function () {
        self.raf = 0;
        self.measure();
      });
    }

    held() {
      return !!(this.section && this.section.classList.contains('aplus-show--held'));
    }

    measure() {
      /* Hand it straight back to the stylesheet on a desktop, and when the merchant has turned
         the hold off. There the panel really is a screen tall, the band it is centred in is the
         one the stylesheet draws, and an inline offset would only be this file's opinion
         overriding a rule that is already right. */
      if (!this.held() || !(this.narrow && this.narrow.matches)) {
        if (this.at !== null) {
          this.at = null;
          this.style.top = '';
        }
        return;
      }

      var cs = getComputedStyle(document.documentElement);
      var head = parseFloat(cs.getPropertyValue('--sticky-header-height')) || 0;
      var dock = parseFloat(cs.getPropertyValue('--mobile-dock-height')) || 0;
      var band = window.innerHeight - head - dock;
      var h = this.offsetHeight;

      /* A stack that already fills the band has no leftover to split: it rests at the header's
         line. Centring one of those would push it down by half of a negative number and hang
         its foot off the bottom of the screen. */
      var top = h >= band ? head : head + Math.max(0, (band - h) / 2);
      top = Math.round(top);

      /* Written only when the number actually changes. This runs on every resize, and on a
         phone the address bar makes a resize out of ordinary scrolling — an offset rewritten
         under a reader who is standing on it moves the panel they are looking at. */
      if (top !== this.at) {
        this.at = top;
        this.style.top = top + 'px';
      }
    }
  }

  customElements.define('anzu-hold', AnzuHold);
})();
